import { Arr } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import Loader from 'react-loader-spinner';
import styled from 'styled-components';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { WorkspaceLiteData } from '../../api/ApiTypes';
import { useSubmitter } from '../../platform/Hook';
import { Button, Colors, Input, Select, remInPx } from '../../ui';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { HelpTip } from '../../ui/Tip';
import { useResolve } from '../Container';
import { ShowIfHasFeature, useFeatureCheck } from '../common/FeatureElements';
import { usePermissions } from '../common/PermissionElements';
import { ButtonStrip, Copy, Flexer } from '../dashboard/elements/LayoutElements';
import { WorkspaceSelector } from './WorkspaceSelector';

const workspaceHelp = `
    This will search through your organizations existing workspaces.  
    For new workspaces, they will need creating first in the Workspaces admin section.
`;

const InputWrapper = styled.div`
    position: relative;
`;

const InputEnter = styled.div`
    position: absolute;
    top: 50%;
    right: .5rem;
    transform: translateY(-50%);
    padding: 0.1em 0.3em;
    background-color: #f9f9f9;
    background-image: linear-gradient(to bottom,#eee,#f9f9f9,#eee);
    border: 1px solid #aaa;
    box-shadow: 0.1em 0.1em 0.2em rgb(0 0 0 / 10%);
    font-size: 0.75rem;
`;

const SelectMenuDesc = styled.span<{ disabled?: boolean }> `
    font-size: .875rem;
    color: ${p => p.disabled ? Colors.text : Colors.textLighter};
    margin-left: .25rem;
`;

const isValid = (email: string) => email.includes('@') && !email.match(/\s/);

const typeOpt = (key: string, label: string, desc: string, disabled = false) => ({ key, label, desc, disabled });
const typeOptsForFree = [
    typeOpt('member', 'Member', ''),
    typeOpt('guest', 'Guest', '— upgrade to access guest users', true),
];
const typeOptsForSubscribed = [
    typeOpt('member', 'Member', '— contributes to your overall billed seats'),
    typeOpt('guest', 'Guest', '— free but has limited permissions'),
];

export const AccountEmailInviteView = () => {

    const api = useResolve(ApiClient);
    const [input, setInput] = useState('');
    const [emails, setEmails] = useState([] as string[]);
    const [type, setRole] = useState('member');
    const [workspaces, setWorkspaces] = useState([] as WorkspaceLiteData[]);
    const accessControl = useFeatureCheck('accessControl');
    const [acTeamWrite] = usePermissions('ac_team:write');
    const typeOpts = useMemo(() => accessControl ? typeOptsForSubscribed : typeOptsForFree, [accessControl]);

    const onReset = useCallback(() => {
        setEmails([]);
        setWorkspaces([]);
    }, []);

    const [onSubmit, working, result] = useSubmitter(async () => {
        await api.exec(v2.admin.invites.send({ emails, type, workspaceIds: workspaces.map(x => x.id) }));
        onReset();
    }, [emails, type, workspaces]);

    return <Flexer flex="column" gap="1">
        <Flexer flex="column" gap=".25">
            <Copy flush bold>
                To:
            </Copy>
            <InputWrapper>
                <Input
                    placeholder="e.g. steve@metroretro.io"
                    value={input}
                    onChange={setInput}
                    itemMode
                    items={emails}
                    onItemAdd={values => setEmails(x => Arr.unique([...x, ...values]).filter(x => isValid(x)))}
                    onItemRemove={values => setEmails(x => x.filter(y => !values.includes(y)))}
                />
                {input.length > 0 && <InputEnter>&crarr; Enter</InputEnter>}
            </InputWrapper>
        </Flexer>
        <Flexer flex="column" gap=".5">
            <Copy bold flush>
                Invite As:
            </Copy>
            <Select items={typeOpts} selected={typeOpts.find(x => x.key === type)} onChange={x => setRole(x.key)}
                renderSelected={(item) => <div>
                    {item?.label}<SelectMenuDesc>{item?.desc}</SelectMenuDesc>
                </div>}
                renderItems={(list, _, onSelect) => <>
                    {list.map(item => <Select.MenuItem key={item.key} disabled={item.disabled} onClick={item.disabled ? null : onSelect(item)}>
                        <Select.MenuItemText>
                            {item.label}
                            <SelectMenuDesc disabled={item.disabled}>{item.desc}</SelectMenuDesc>
                        </Select.MenuItemText>
                    </Select.MenuItem>)}
                </>
                }
            />
        </Flexer>
        {type === 'member' && acTeamWrite && <>
            <ShowIfHasFeature name="workspaces">
                <Flexer flex="column" gap=".5">
                    <Copy bold flush>
                        Add To Workspaces:
                        <HelpTip content={workspaceHelp} />
                    </Copy>
                    <WorkspaceSelector items={workspaces} onSelect={setWorkspaces} />
                </Flexer>
            </ShowIfHasFeature>
        </>}
        <Flexer flex="row-reverse" align="center" justify="space-between" margin=".5 0 0 0">
            <ButtonStrip align="right">
                <Button onAction={onReset}>Reset</Button>
                <Button onAction={onSubmit} disabled={working || !emails.length} variant="primary">
                    {working ? 'Sending...' : `Send Invite${emails.length > 1 ? 's' : ''} ${emails.length > 0 ? `(${emails.length})` : ''}`}
                    {working && <Loader
                        type="TailSpin"
                        color="#fff"
                        height={remInPx(1.2)}
                        width={remInPx(1.2)}
                    />}
                </Button>
            </ButtonStrip>
            <ResultInfoForSubmitter result={result} onSuccess="Invitations have been sent!" />
        </Flexer>
    </Flexer>;
};


